import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setConsentState, track, type ConsentState } from "@/lib/tracking";
import { loadMarketingScripts } from "@/lib/analytics";

const STORAGE_KEY = "sr_lgpd_consent_v1";
const CURRENT_VERSION = 1;

const DEFAULT_STATE: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
  decided: false,
  version: CURRENT_VERSION,
  updatedAt: new Date(0).toISOString(),
};

const readStored = (): ConsentState => {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CURRENT_VERSION) return DEFAULT_STATE;
    return { ...DEFAULT_STATE, ...parsed, necessary: true };
  } catch {
    return DEFAULT_STATE;
  }
};

const persist = (state: ConsentState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage pode falhar em modo privado
  }
};

export type ConsentContextValue = {
  state: ConsentState;
  acceptAll: () => void;
  rejectAll: () => void;
  customize: (next: { analytics: boolean; marketing: boolean }) => void;
  reset: () => void;
};

const ConsentContext = createContext<ConsentContextValue | undefined>(
  undefined
);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState>(() => readStored());

  // Sincroniza com lib/tracking ao montar e a cada mudança
  useEffect(() => {
    setConsentState({
      analytics: state.analytics,
      marketing: state.marketing,
    });
    // Marketing consentido → carrega Meta Pixel / GA4 / Ads (config-gated)
    if (state.marketing) loadMarketingScripts();
  }, [state.analytics, state.marketing]);

  const update = useCallback(
    (next: Pick<ConsentState, "analytics" | "marketing">) => {
      const newState: ConsentState = {
        ...DEFAULT_STATE,
        ...next,
        decided: true,
        version: CURRENT_VERSION,
        updatedAt: new Date().toISOString(),
      };
      setState(newState);
      persist(newState);
      track(
        next.analytics || next.marketing
          ? "consent_granted"
          : "consent_revoked",
        {
          analytics: next.analytics,
          marketing: next.marketing,
        }
      );
    },
    []
  );

  const acceptAll = useCallback(
    () => update({ analytics: true, marketing: true }),
    [update]
  );
  const rejectAll = useCallback(
    () => update({ analytics: false, marketing: false }),
    [update]
  );
  const customize = useCallback(
    (next: { analytics: boolean; marketing: boolean }) => update(next),
    [update]
  );

  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({ state, acceptAll, rejectAll, customize, reset }),
    [state, acceptAll, rejectAll, customize, reset]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent precisa de <ConsentProvider> acima na árvore");
  }
  return ctx;
}
